/**
 * Rank slots (user, 19/10/2026: "generate 10 slots with the 10 of the
 * dynamic list" — tied to a rank, not a list): a group on all lists gets
 * #1…#N. The group's slots take the ranks in their order ("use"), new slots
 * carry the ranks left over ("create"); more slots than ranks go round
 * again. A rank replaces a fixed list for that group; other groups on a
 * slot keep their choice. The ranks follow the group every day.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DraftSlot } from '../../../../drop-generator.types';
import { dropKey } from './group-keyed';
import { newSlot } from './slot-ops';

export interface RankSlotsPlan {
  /** The group's slots that get a rank. */
  use: number;
  /** New slots for the ranks left over. */
  create: number;
}

/** The group's switched-on drops take ranks; a switched-off one is left as it is (it sends nothing). */
const takesRank = (s: DraftSlot, groupId: string) => s.enabled && s.listGroupIds.includes(groupId);

export function rankSlotsPlan(slots: DraftSlot[], groupId: string, ranks: number): RankSlotsPlan {
  const use = slots.filter((s) => takesRank(s, groupId)).length;
  return { use, create: Math.max(0, ranks - use) };
}

const withRank = (slot: DraftSlot, groupId: string, rank: number): DraftSlot => ({
  ...slot,
  listsByGroup: dropKey(slot.listsByGroup, groupId),
  listsRankByGroup: { ...(slot.listsRankByGroup ?? {}), [groupId]: rank },
});

export function rankSlots(slots: DraftSlot[], groupId: string, ranks: number): DraftSlot[] {
  if (ranks < 1) return slots;
  let k = 0;
  const out = slots.map((s) => (takesRank(s, groupId) ? withRank(s, groupId, (k++ % ranks) + 1) : s));
  for (; k < ranks; k += 1) out.push(withRank(newSlot([groupId]), groupId, k + 1));
  return out;
}
